

















































































































import { Vue, Component, Prop } from "vue-property-decorator";
import draggable from "vuedraggable";
import { audiobooks } from "@/config/host.config";
import Progress from "@/components/app/images/progress.vue";
import AudioPlayerBar from "@/components/app/audio-player-bar.vue";

const streamTemplate = `${audiobooks.stream}?audiobookId={audiobookId}&chapterId={chapterId}`;

interface IAudiobookChapter {
  _id: string;
  name: { ru?: string; en?: string; [key: string]: string | undefined };
  filename: string;
  file_type: string;
  sort_order: number;
  language: string;
  duration: number;
}

@Component({
  components: { ProgressComponent: Progress, draggable, AudioPlayerBar },
})
class AudiobookChaptersEditor extends Vue {
  @Prop({ type: String, required: true }) audiobookId!: string;
  @Prop({ type: Array, default: () => [] })
  initialChapters!: IAudiobookChapter[];
  @Prop({ type: Object, default: () => ({}) })
  poster!: Record<string, string>;

  chapters: IAudiobookChapter[] = [];
  loading = false;
  uploading = false;
  uploadProgress = 0;
  totalBytes = 0;
  uploadedBytes = 0;
  streamTemplate = streamTemplate;
  currentParams: Record<string, string | number> = {};
  currentTitle = "";

  $refs!: {
    fileInput: HTMLInputElement;
  };

  getLocalizedName(
    name: { [key: string]: string | undefined } | undefined
  ): string {
    if (!name) return "—";
    return name[this.$i18n.locale] || name.ru || name.en || "—";
  }

  formatDuration(seconds: number | undefined): string {
    if (!seconds) return "—";
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    if (h > 0) {
      return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
    }
    return `${m}:${String(s).padStart(2, "0")}`;
  }

  playChapter(chapter: IAudiobookChapter): void {
    this.currentParams = {
      audiobookId: this.audiobookId,
      chapterId: chapter._id,
    };
    this.currentTitle = this.getLocalizedName(chapter.name);
  }

  stopPlayback(): void {
    this.currentParams = {};
    this.currentTitle = "";
  }

  async fetchChapters(): Promise<void> {
    if (!this.audiobookId) return;
    this.loading = true;
    try {
      const res = await this.$axios.get(
        `${audiobooks.books}/${this.audiobookId}`
      );
      if (res.status === 200) {
        this.chapters = (res.data.src && res.data.src.file) || [];
        this.$emit("update:chapters", this.chapters);
      }
    } catch (e) {
      console.log(e);
    } finally {
      this.loading = false;
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || !input.files.length) return;

    const files = Array.from(input.files);
    input.value = "";

    this.totalBytes = files.reduce((sum, f) => sum + f.size, 0);
    this.uploadedBytes = 0;
    this.uploading = true;
    this.uploadFilesSequentially(files);
  }

  async uploadFilesSequentially(files: File[]): Promise<void> {
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      try {
        await this.uploadFile(file);
        this.uploadedBytes += file.size;
      } catch (e) {
        console.log(e);
        this.$notify({
          group: "global",
          text: String(
            this.$i18n.t("audiobooks.uploadError", { name: file.name })
          ),
          type: "warn",
        });
      } finally {
        delete files[i];
      }
    }
    this.uploading = false;
    this.uploadProgress = 0;
    this.fetchChapters();
  }

  uploadFile(file: File): Promise<void> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      const url = `${audiobooks.books}/upload?audiobookId=${encodeURIComponent(
        this.audiobookId
      )}`;

      xhr.upload.onprogress = (e: ProgressEvent) => {
        if (!e.lengthComputable) return;
        const newProgress =
          ((this.uploadedBytes + e.loaded) / this.totalBytes) * 100;
        this.uploadProgress = Math.max(this.uploadProgress, newProgress);
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          this.$notify({
            group: "global",
            text: String(this.$i18n.t("error.200")),
          });
          resolve();
        } else {
          reject(new Error(`HTTP ${xhr.status}: ${xhr.responseText}`));
        }
      };

      xhr.onerror = () => reject(new Error("Network error"));
      xhr.onabort = () => reject(new Error("Aborted"));
      xhr.ontimeout = () => reject(new Error("Timeout"));

      xhr.open("POST", url, true);
      xhr.setRequestHeader(
        "Authorization",
        `Bearer ${this.$store.getters.getAccess}`
      );
      xhr.setRequestHeader("X-File-Name", encodeURIComponent(file.name));
      xhr.setRequestHeader("X-File-Size", String(file.size));
      xhr.timeout = 600000;

      const form = new FormData();
      form.append("file", file, file.name);
      xhr.send(form);
    });
  }

  async onChaptersReordered(): Promise<void> {
    this.chapters.forEach((ch, i) => {
      ch.sort_order = i;
    });
    try {
      const res = await this.$axios.put(
        `${audiobooks.books}/${this.audiobookId}`,
        {
          src: { file: this.chapters, vertical_poster: this.poster },
        }
      );
      if (res.status === 200) {
        this.$emit("update:chapters", this.chapters);
        this.$notify({
          group: "global",
          text: String(this.$i18n.t("error.200")),
        });
      }
    } catch (e) {
      console.log(e);
      this.$notify({
        group: "global",
        text: String(this.$i18n.t("alerts.emptyUpdate")),
        type: "warn",
      });
    }
  }

  async removeChapter(index: number): Promise<void> {
    const ok = await this.$bvModal.msgBoxConfirm(
      String(this.$t("audiobooks.confirmDeleteChapter")),
      {
        title: String(this.$i18n.t("buttons.delete")),
        okVariant: "danger",
        cancelVariant: "secondary",
      }
    );
    if (!ok) return;

    try {
      const chapter = this.chapters[index];
      const url = `${audiobooks.stream}/${this.audiobookId}/chapters/${chapter._id}`;
      const res = await this.$axios.delete(url);
      if (res.status === 200) {
        this.$notify({
          group: "global",
          text: String(this.$i18n.t("error.200")),
        });
        this.fetchChapters();
      }
    } catch (e) {
      console.log(e);
      this.$notify({
        group: "global",
        text: String(this.$i18n.t("alerts.emptyUpdate")),
        type: "warn",
      });
    }
  }

  mounted(): void {
    this.chapters = [...this.initialChapters];
  }
}
export default AudiobookChaptersEditor;
